
import { RevButtonRounded } from '@ds/components/ButtonRounded'
import { RevButtonTiny } from '@ds/components/ButtonTiny'
import { RevPopover } from '@ds/components/Popover'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconHeart } from '@ds/icons/IconHeart'
import { IconHeartFilled } from '@ds/icons/IconHeartFilled'
import isEmpty from 'lodash/isEmpty'
import { mapGetters } from 'vuex'

import {
  createFavoriteForUser,
  deleteUserFavorite,
  getFavoriteFromProduct,
} from '@http/endpoints'
import logger from '@logger'
import { ROUTES } from '@router'
import { PAGE_TYPE_BY_ROUTE_NAME } from '@tracking/constants'
import { trackAddToMyFavorites, trackClick } from '@tracking/events'

import {
  LOG_ERROR_MESSAGE,
  MY_FAVORITES_ACTIONS,
  MY_FAVORITES_STORAGE_KEY,
} from './MyFavorites.constants'
import translations from './MyFavorites.translations'

export default {
  components: {
    RevButtonTiny,
    RevButtonRounded,
    RevPopover,
    RevTooltip,
  },

  props: {
    isRounded: {
      type: Boolean,
      default: false,
    },

    trackingInfo: {
      type: Object,
      default: () => ({}),
    },

    zone: {
      type: String,
      default: 'detail',
    },

    favoriteId: {
      type: String,
      default: '',
    },

    productUuid: {
      type: String,
      required: true,
    },

    gradeId: {
      type: Number,
      required: true,
    },

    price: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      itemFavoriteId: this.favoriteId,
    }
  },

  computed: {
    ...mapGetters({
      isAuthenticated: 'auth/status',
      isNewAuthEnabled: 'auth/isNewAuthEnabled',
    }),

    translations: () => translations,

    isFavorite() {
      return !isEmpty(this.itemFavoriteId)
    },

    shouldDisplayMyFavoritesToggle() {
      return this.$config.FF_MY_FAVORITES_ENABLED
    },

    buttonLabel() {
      return this.isFavorite
        ? this.$t(translations.favoritesButtonRemoveLabel)
        : this.$t(translations.favoritesButtonAddLabel)
    },

    buttonTooltip() {
      return this.isFavorite
        ? this.$t(translations.favoritesButtonRemoveTooltip)
        : this.$t(translations.favoritesButtonAddTooltip)
    },

    heartComponentVariant() {
      return this.isFavorite ? IconHeartFilled : IconHeart
    },

    hasPendingAction() {
      return Boolean(sessionStorage.getItem(MY_FAVORITES_STORAGE_KEY))
    },
  },

  watch: {
    isAuthenticated: {
      handler(isAuthenticated) {
        if (isAuthenticated) {
          if (this.hasPendingAction) {
            this.executePendingAction()
          } else {
            this.getFavoriteFromProduct()
          }
        }
      },
      immediate: true,
    },
  },

  methods: {
    trackAddClick() {
      trackAddToMyFavorites(this.trackingInfo)
    },

    trackRemoveClick() {
      const routeName = this.$route.name

      trackClick({
        name: 'remove_from_my_favorites',
        zone: this.zone,
        pageType: PAGE_TYPE_BY_ROUTE_NAME[routeName],
      })
    },

    isPendingFavoriteMatchingProduct({ productUuid, gradeId }) {
      const areProductUuidsMatching = productUuid === this.productUuid
      const areGradeIdsMatching = gradeId === this.gradeId

      return areProductUuidsMatching && areGradeIdsMatching
    },

    executePendingAction() {
      if (!this.hasPendingAction || !this.isAuthenticated) {
        return
      }

      const pendingAction = sessionStorage.getItem(MY_FAVORITES_STORAGE_KEY)

      try {
        const { action, productUuid, gradeId } = JSON.parse(pendingAction)

        if (!this.isPendingFavoriteMatchingProduct({ productUuid, gradeId })) {
          return
        }

        if (action === MY_FAVORITES_ACTIONS.ADD) {
          this.addToFavorites()
        } else if (action === MY_FAVORITES_ACTIONS.REMOVE) {
          this.removeFromFavorites()
        }
      } catch {
        // fails silently (json structure exception)
      }
    },

    clearPendingAction() {
      sessionStorage.removeItem(MY_FAVORITES_STORAGE_KEY)
    },

    storePendingAction(action) {
      const pendingAction = JSON.stringify({
        action,
        productUuid: this.productUuid,
        gradeId: this.gradeId,
      })

      sessionStorage.setItem(MY_FAVORITES_STORAGE_KEY, pendingAction)
    },

    requestAuthentication() {
      const next = this.$route.fullPath

      // [LIF-1229] TODO: remove legacy auth route when the new one is on production
      let authLink = {
        name: ROUTES.AUTH.REGISTER,
        query: {
          ...this.$route.query,
          next,
        },
      }

      if (this.isNewAuthEnabled) {
        authLink = {
          name: ROUTES.AUTH_OAUTH2.LOGIN,
          query: {
            ...this.$route.query,
            next,

            // needed for the info toast on Auth page
            info: 'my-favorites',
          },
        }
      }

      this.$navigation.push(authLink)
    },

    async getFavoriteFromProduct() {
      if (this.isFavorite || !this.isAuthenticated) {
        return
      }

      try {
        const { productUuid, gradeId } = this

        const {
          payload: { favoriteId },
        } = await this.$store.dispatch('http/request', {
          request: getFavoriteFromProduct,
          pathParams: { productUuid, gradeId },
        })

        this.itemFavoriteId = favoriteId
      } catch (error) {
        if (error.status_code === 404) {
          return
        }

        logger.error(LOG_ERROR_MESSAGE.CHECK, error)
      }
    },

    async addToFavorites() {
      try {
        const body = {
          productUuid: this.productUuid,
          gradeId: this.gradeId,
          price: this.price,
        }

        const {
          payload: { favoriteId },
        } = await this.$store.dispatch('http/request', {
          request: createFavoriteForUser,
          body,
        })

        this.itemFavoriteId = favoriteId
        this.clearPendingAction()
      } catch (error) {
        logger.error(LOG_ERROR_MESSAGE.ADD, error)
      }
    },

    async removeFromFavorites() {
      try {
        const favoriteId = this.itemFavoriteId

        await this.$store.dispatch('http/request', {
          request: deleteUserFavorite,
          pathParams: { favoriteId },
        })

        this.itemFavoriteId = ''
        this.clearPendingAction()
      } catch (error) {
        logger.error(LOG_ERROR_MESSAGE.REMOVE, error)
      }
    },

    handleClick() {
      const doTrackClick = this.isFavorite
        ? this.trackRemoveClick
        : this.trackAddClick
      doTrackClick()

      if (!this.isAuthenticated) {
        this.storePendingAction(MY_FAVORITES_ACTIONS.ADD)

        this.requestAuthentication()

        return
      }

      const doToggleFavorite = this.isFavorite
        ? this.removeFromFavorites
        : this.addToFavorites
      doToggleFavorite()
    },
  },
}
